import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isGR7_1RenovationOrDevelopment,
	isGR7_3HomeOfficeOrBusiness,
	isGR7_4InstallationOfEnseigne
} from '../__index.utils'

export const STEP3FIELDS = {
	mainUseOfExistingBuilding: 'mainUseOfExistingBuilding',
	doYouWantToChangeCurrentUse: 'doYouWantToChangeCurrentUse',
	projectedUse: 'projectedUse',
	existingMainBuildingIsShared: 'existingMainBuildingIsShared'
	// isResidentialOrOther: 'isResidentialOrOther',
	// structureType: 'structureType',
	// useOfBuildingOrLand: 'useOfBuildingOrLand',
} as const

export type Step3Fields = keyof typeof STEP3FIELDS

/**
 * useStep3State - Custom hook to manage the state for step 3 of the GR7 commercial Permit form.
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 3.
 */

export function useStep3State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR7_1 = isGR7_1RenovationOrDevelopment(answers?.whatToDo)
	const isGR7_3 = isGR7_3HomeOfficeOrBusiness(answers?.whatToDo)
	const isGR7_4 = isGR7_4InstallationOfEnseigne(answers?.whatToDo)

	return {
		//  "Quel est l'usage actuel du bâtiment existant?"
		mainUseOfExistingBuilding: {
			name: 'mainUseOfExistingBuilding',
			label:
				pageAssets?.request_form_commercialPermit_mainUseOfExistingBuilding,
			labelKey: 'request_form_commercialPermit_mainUseOfExistingBuilding',
			value: answers?.mainUseOfExistingBuilding?.toString() || '',
			required: isGR7_1 || isGR7_3 || isGR7_4,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		},
		// Souhaitez-vous effectuer un changement à l'usage actuel du bâtiment?
		doYouWantToChangeCurrentUse: {
			name: 'doYouWantToChangeCurrentUse',
			label:
				pageAssets?.request_form_commercialPermit_doYouWantToChangeCurrentUse,
			labelKey: 'request_form_commercialPermit_doYouWantToChangeCurrentUse',
			value: answers?.doYouWantToChangeCurrentUse?.toString() || '',
			required: isGR7_1,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		},
		// Quel est l'usage projeté?
		projectedUse: {
			name: 'projectedUse',
			label: pageAssets?.request_form_commercialPermit_projectedUse,
			labelKey: 'request_form_commercialPermit_projectedUse',
			value: answers?.projectedUse?.toString() || '',
			required:
				isGR7_1 && answers?.doYouWantToChangeCurrentUse?.toString() === 'true',
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		},
		// Le bâtiment principal existant est-il ou fait-il partie d'une copropriété?
		existingMainBuildingIsShared: {
			name: 'existingMainBuildingIsShared',
			label:
				pageAssets?.request_form_buildingDemolitionOrMovingPermit_isTheNewBuildingPartOfAProperty,
			labelKey:
				'request_form_buildingDemolitionOrMovingPermit_isTheNewBuildingPartOfAProperty',
			value: answers?.existingMainBuildingIsShared?.toString() || '',
			required: isGR7_1 || isGR7_3 || isGR7_4,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_3
		}
	}
}
